import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import TotalIcon from '@mui/icons-material/Dashboard';
import Add from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllCompanies } from '../store/company.selectors';
import { Box } from '@mui/material';
import AddCompanyModal from './AddCompany';
import { resetCompany } from '../store/company.actions';
import {
  AddButton,
  HeaderSection,
  StatsSection,
  ButtonSection,
  StatItem,
  StatValue,
  StatLabel,
  VerticalDivider,
  flexAlignCenterGap,
} from '../../../utils/common/styles/commonStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import PropTypes from 'prop-types';

const SearchHeader = (props) => {
  const { searchTerm, setSearchTerm, handleOpenModal } = props;
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const companies = useSelector(selectAllCompanies);
  const handleCloseModal = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    dispatch(resetCompany());
    setIsModalOpen(false);
  };
  const isActive = (endDate) => {
    if (!endDate) return false;
    const subscriptionEnd = new Date(endDate);
    const today = new Date();
    return subscriptionEnd > today;
  };
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const totalCompanies = companies?.length || 0;
  const activeCompanies =
    companies?.filter((comp) => comp.subscriptionEndDate && isActive(comp.subscriptionEndDate))
      .length || 0;
  const inactiveCompanies =
    companies?.filter((comp) => comp.subscriptionEndDate && !isActive(comp.subscriptionEndDate))
      .length || 0;

  return (
    <HeaderSection>
      <Box sx={{ ...flexAlignCenterGap(2), flex: 1 }}>
        <TextField
          placeholder="Search companies..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            minWidth: 250,
            backgroundColor: 'background.paper',
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.15)',
                borderColor: '#1976d2',
              },
              '&.Mui-focused': {
                boxShadow:
                  '0 0 0 4px rgba(25, 118, 210, 0.15), 0 4px 16px rgba(25, 118, 210, 0.25)',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 0.6,
            },
          }}
        />
      </Box>

      <StatsSection
        sx={{
          ...flexAlignCenterGap(3),
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        }}
      >
        <StatItem sx={{ padding: '12px 16px' }}>
          <TotalIcon sx={{ fontSize: 20, color: '#667eea', transition: 'all 0.3s ease' }} />
          <Box display="flex" alignItems="baseline">
            <StatValue sx={{ color: 'primary.alt', transition: 'all 0.3s ease' }}>
              {totalCompanies}
            </StatValue>
            <StatLabel>Company</StatLabel>
          </Box>
        </StatItem>

        <StatItem sx={{ padding: '12px 16px' }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 20, color: 'success.main', transition: 'all 0.3s ease' }}
          />
          <Box display="flex" alignItems="baseline">
            <StatValue sx={{ color: 'success.main', transition: 'all 0.3s ease' }}>
              {activeCompanies}
            </StatValue>
            <StatLabel>Active</StatLabel>
          </Box>
        </StatItem>

        <StatItem sx={{ padding: '12px 16px' }}>
          <ReportOutlinedIcon
            sx={{ fontSize: 20, color: 'warning.main', transition: 'all 0.3s ease' }}
          />
          <Box display="flex" alignItems="baseline">
            <StatValue sx={{ color: 'warning.main', transition: 'all 0.3s ease' }}>
              {inactiveCompanies}
            </StatValue>
            <StatLabel>Expired</StatLabel>
          </Box>
        </StatItem>
      </StatsSection>

      <VerticalDivider orientation="vertical" flexItem sx={{ margin: '0 16px' }} />

      <ButtonSection>
        <AddButton
          variant="contained"
          onClick={handleOpenModal}
          startIcon={<Add />}
          fullWidth={isSmallScreen}
        >
          {isSmallScreen ? 'Add' : 'Add Company'}
        </AddButton>
      </ButtonSection>
      <AddCompanyModal open={isModalOpen} handleClose={handleCloseModal} mode="add" />
    </HeaderSection>
  );
};
export default SearchHeader;

SearchHeader.propTypes = {
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  handleOpenModal: PropTypes.func,
};
